import { SubscribeButton } from "@/components";
import { useAppProperties } from "@/hooks";
import { Lock, StopSign, TriangleWarning } from "@/icons";
import { useTranslate } from "@/providers/context";
import { cn } from "@/utils";

type ScreenType = "locked" | "error" | "banned";

const ICON_SIZE = { height: 68, width: 68 };

const ICON_STYLES: Partial<Record<ScreenType, string>> = {
  locked: "text-item-primary",
};

const TEXT_STYLES: Partial<Record<ScreenType, string>> = {
  locked: "text-item-secondary max-w-[284px]",
  error: "max-w-[332px]",
};

const ICONS: Record<ScreenType, JSX.Element> = {
  locked: <Lock {...ICON_SIZE} />,
  error: <TriangleWarning {...ICON_SIZE} />,
  banned: <StopSign {...ICON_SIZE} />,
};

const HEADINGS: Partial<Record<ScreenType, string>> = {
  locked: "Community Locked",
};

const TEXT: Record<ScreenType, string> = {
  locked: "Community is only available for people with an active subscription.",
  error: "Failed to load. Please refresh the page. If the issue persists",
  banned: "You have been banned from this feed.",
};

const BUTTONS: Partial<Record<ScreenType, JSX.Element>> = {
  locked: <SubscribeButton />,
};

export const EmptyScreen = ({ screenType }: { screenType: ScreenType }) => {
  const { translate } = useTranslate();
  const { data } = useAppProperties();

  const headingString = HEADINGS[screenType] ?? "";

  return (
    <div className="flex h-full items-center justify-center pb-[94px]">
      <div
        className={cn(
          "flex flex-col items-center gap-4 text-item-tertiary",
          TEXT_STYLES[screenType],
        )}
      >
        <div className={cn(ICON_STYLES[screenType])}>{ICONS[screenType]}</div>
        <div className="flex flex-col items-center gap-2">
          {headingString && (
            <div className="h5-bold text-item-primary">
              {translate(headingString)}
            </div>
          )}
          <div className="p1 text-center">
            {translate(TEXT[screenType])}
            {screenType === "error" && (
              <a
                href={data?.AppSupportURL ?? ""}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 cursor-pointer lowercase underline underline-offset-2"
              >
                {translate("Contact us.")}
              </a>
            )}
          </div>
        </div>
        {BUTTONS[screenType] && <div>{BUTTONS[screenType]}</div>}
      </div>
    </div>
  );
};
